import {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
    type KeyboardEvent,
    type ReactNode,
    type RefObject
} from 'react';
import { createPortal } from 'react-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useFloatingPosition, type FloatingPosition } from '../../hooks/useFloatingPosition';
import { usePopoverDismiss } from '../../hooks/usePopoverDismiss';
import type { QuickReplyItem } from './quickReplyItem';
import { filterQuickReplies } from './filterQuickReplies';
import { QuickRepliesList } from './QuickRepliesList';

const POPOVER_MAX_HEIGHT = 360;
const POPOVER_MAX_WIDTH = 420;
const VIEWPORT_MARGIN = 8;
const POPOVER_Z_INDEX = 9000; // below toasts (z-[9999]), same layer as FloatingMenu

interface QuickRepliesPopoverProps<T extends QuickReplyItem> {
    anchorRef: RefObject<HTMLElement>;
    buttonRef: RefObject<HTMLButtonElement>;
    replies: T[];
    isFetching: boolean;
    errorMessage: string | null;
    onRetry: () => void;
    onPick: (reply: T) => void;
    onClose: () => void;
    footer?: ReactNode;
}

function availableHeight(position: FloatingPosition): number {
    const edge = position.placement === 'top' ? position.bottom : position.top;
    return Math.min(POPOVER_MAX_HEIGHT, window.innerHeight - (edge ?? 0) - VIEWPORT_MARGIN);
}

function QuickRepliesPopover<T extends QuickReplyItem>({
    anchorRef,
    buttonRef,
    replies,
    isFetching,
    errorMessage,
    onRetry,
    onPick,
    onClose,
    footer
}: QuickRepliesPopoverProps<T>) {
    const popoverRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const baseId = useId();
    const listId = `${baseId}-list`;
    const optionId = useCallback((index: number) => `${baseId}-option-${index}`, [baseId]);

    const position = useFloatingPosition({
        open: true,
        triggerRef: anchorRef,
        preferUp: true,
        matchTriggerWidth: true,
        estimatedHeight: POPOVER_MAX_HEIGHT
    });
    const insideRefs = useMemo(() => [popoverRef, buttonRef], [buttonRef]);
    usePopoverDismiss({ open: true, onClose, insideRefs });

    const filtered = useMemo(() => filterQuickReplies(replies, query), [replies, query]);
    const safeIndex = Math.min(activeIndex, Math.max(filtered.length - 1, 0));
    const isPositioned = position !== null;

    useEffect(() => {
        if (isPositioned) searchRef.current?.focus({ preventScroll: true });
    }, [isPositioned]);

    useEffect(() => {
        listRef.current
            ?.querySelector(`[data-index="${safeIndex}"]`)
            ?.scrollIntoView({ block: 'nearest' });
    }, [safeIndex]);

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            const step = e.key === 'ArrowDown' ? 1 : -1;
            setActiveIndex(Math.min(Math.max(safeIndex + step, 0), filtered.length - 1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const reply = filtered[safeIndex];
            if (reply) onPick(reply);
        } else if (e.key === 'Escape') {
            // Stop here so a host drawer's own Escape handler doesn't close with the popover.
            e.stopPropagation();
            onClose();
            buttonRef.current?.focus();
        }
    };

    if (!position) return null;

    return createPortal(
        <div
            ref={popoverRef}
            role="dialog"
            aria-label="Quick replies"
            style={{
                position: 'fixed',
                zIndex: POPOVER_Z_INDEX,
                top: position.top,
                bottom: position.bottom,
                left: position.left,
                width: position.width,
                maxWidth: Math.min(POPOVER_MAX_WIDTH, window.innerWidth - 2 * VIEWPORT_MARGIN),
                maxHeight: availableHeight(position)
            }}
            className="bg-white rounded-2xl shadow-xl border border-gray-200 flex flex-col overflow-hidden"
        >
            <div className="p-2 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center h-9 bg-white border border-gray-200 rounded-xl px-3">
                    <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                    <input
                        ref={searchRef}
                        type="text"
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setActiveIndex(0);
                        }}
                        onKeyDown={handleKeyDown}
                        placeholder="Search quick replies"
                        role="combobox"
                        aria-expanded={filtered.length > 0}
                        aria-controls={listId}
                        aria-autocomplete="list"
                        aria-activedescendant={
                            filtered.length > 0 ? optionId(safeIndex) : undefined
                        }
                        className="flex-1 min-w-0 bg-transparent outline-none text-sm text-gray-700 placeholder-gray-400"
                    />
                </div>
            </div>
            <QuickRepliesList
                listId={listId}
                listRef={listRef}
                optionId={optionId}
                allCount={replies.length}
                filtered={filtered}
                query={query}
                activeIndex={safeIndex}
                isFetching={isFetching}
                errorMessage={errorMessage}
                onRetry={onRetry}
                onHover={setActiveIndex}
                onPick={onPick}
            />
            {footer}
        </div>,
        document.body
    );
}

export { QuickRepliesPopover };
